import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AuthPopinService } from '../auth-popin.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AuthenticationService as SDKAuthentication } from '@adeo/ngx-kozikaza-api';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../auth.service';
import { ToastrService } from '../../../shared/services/utilities/toastr.service';
import { KzIconsRegistryService } from '../../../kz-ui/ui/icon/kz-icons-registry.service';
import { kzIconsDiscover } from '../../../kz-ui/ui/icon/generated-icons/kzIcons-discover.icon';
import { kzIconsKozikaza } from '../../../kz-ui/ui/icon/generated-icons/kzIcons-kozikaza.icon';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  forgotPasswordForm: UntypedFormGroup;
  hasError = false;
  error: any;
  submitted = false;
  smsAlreadySend = false;
  magicLinkAlreadySend = false;
  forgotPasswordSubscription: Subscription;
  updatePasswordSubscription: Subscription;
  params: any;
  mode: string;
  gtmForm = 'forgetPassword_form';
  gtmCancel = 'forgetPassword_cancel';
  gtmSubmit = 'forgetPassword_next';
  showPassword = false;

  constructor(
    private authPopinService: AuthPopinService,
    private fb: UntypedFormBuilder,
    private sdkAuthenticationService: SDKAuthentication,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toastr: ToastrService,
    private authService: AuthService,
    private kzIconsRegistryService: KzIconsRegistryService,
    private cdr: ChangeDetectorRef,
  ) {
    this.kzIconsRegistryService.registerIcons([kzIconsDiscover, kzIconsKozikaza]);
    this.forgotPasswordForm = this.fb.group({
      emailOrPhone: ['', [Validators.required]]
    });
  }

  ngOnInit() {
    this.initFields();
  }

  initFields(): void {
    this.params = this.activatedRoute.snapshot.queryParams;

    if (this.params.verification_code && this.params.email) {
      this.mode = 'email';
      this.gtmForm = 'forgetPasswordVerificationCode_form';
      this.gtmCancel = 'forgetPasswordVerificationCode_cancel';
      this.gtmSubmit = 'forgetPasswordVerificationCode_next';
    }

    if (this.params.verification_code && this.params.email) {
      // R5 : Set value for email and disable field
      this.forgotPasswordForm.get('emailOrPhone').setValue(
        (this.params.email) ? this.params.email : this.forgotPasswordForm.get('emailOrPhone').value
      );
      this.forgotPasswordForm.get('emailOrPhone').disable();

      // R5: Add control verification code and set value and disable field
      this.forgotPasswordForm.addControl('verification_code', new UntypedFormControl(
        ((this.params && this.params.verification_code) ? this.params.verification_code : ''),
        [Validators.required]
      ));
      if (this.params && this.params.verification_code) {
        this.forgotPasswordForm.get('verification_code').disable();
      }
    }

    if (this.params.token) {
      this.forgotPasswordForm.removeControl('emailOrPhone');
    }

    if ((this.params.verification_code && this.params.email) || this.params.token) {
      // all mode ( R5 and Legacy);
      this.forgotPasswordForm.addControl('password', new UntypedFormControl('', [Validators.required, Validators.pattern(/^(?!.* )(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/)]));
    }
  }

  isFieldInvalid(name: string): boolean {
    if (!this.forgotPasswordForm) {
      return false;
    }

    const control = this.forgotPasswordForm.get(name);
    if (!control) {
      return false;
    }
    return (this.hasError || control.dirty || control.touched) && control.invalid;
  }

  save(): void {
    this.error = null;
    if (this.forgotPasswordForm.valid) {
      this.forgotPasswordForm.disable();
      this.hasError = false;
      this.submitted = true;

      if ((this.params && this.params.email && this.params.verification_code) || this.smsAlreadySend) {
        this.updatePassword();
      } else if ((this.params && this.params.token)) {
        this.legacyUpdatePassword();
      } else {
        this.requestPassword();
      }
    } else {
      this.hasError = true;
    }
  }

  updatePassword(): void {
    this.updatePasswordSubscription = this.sdkAuthenticationService.updatePassword({
      email: this.forgotPasswordForm.get('emailOrPhone').value,
      password: this.forgotPasswordForm.get('password').value,
      verification_code: this.forgotPasswordForm.get('verification_code').value
    })
      .pipe(
        finalize(() => {
          this.forgotPasswordForm.enable();
          this.submitted = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(
        () => {
          this.authPopinService.close();
          this.toastr.info($localize`:@@toast.password_updated:Your password has been changed`);
        },
        (err) => {
          this.error = err.error;
          this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
        }
      );
  }

  updateEmail(event): void {
    if (event && event.target && event.target.value && this.magicLinkAlreadySend) {
      this.magicLinkAlreadySend = false;
    }
  }

  legacyUpdatePassword(): void {
    this.updatePasswordSubscription = this.sdkAuthenticationService.legacyUpdatePassword({
      password: this.forgotPasswordForm.get('password').value,
      token: this.params.token
    })
      .pipe(
        finalize(() => {
          this.forgotPasswordForm.enable();
          this.submitted = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(
        () => {
          this.authPopinService.close();
          this.toastr.info($localize`:@@toast.password_updated:Your password has been changed`);
        },
        (err) => {
          this.error = err.error;
          this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
        }
      );
  }

  requestPassword(): void {
    this.magicLinkAlreadySend = false;

    let params: any;
    if (this.forgotPasswordForm.get('emailOrPhone').value.indexOf('@') !== -1) {
      params = {
        email: this.forgotPasswordForm.get('emailOrPhone').value,
        redirect_uri: environment.REACHFIVE_REDIRECT_URI + '/auth(auth:authentification/forgot-password)'
      };
      this.mode = 'email';
    } else {
      params = {
        phone_number: this.forgotPasswordForm.get('emailOrPhone').value,
        redirect_uri: environment.REACHFIVE_REDIRECT_URI + '/auth(auth:authentification/forgot-password)'
      };
      this.mode = 'sms';
    }

    this.forgotPasswordSubscription = this.sdkAuthenticationService.requestPasswordReset(params)
      .pipe(
        finalize(() => {
          this.submitted = false;
          this.cdr.markForCheck();
        })
      ).subscribe(
        () => {
          if (this.mode === 'email') {
            this.forgotPasswordForm.enable();
            this.magicLinkAlreadySend = true;
          } else {
            this.smsAlreadySend = true;
            this.initFields();
          }
        },
        (err) => {
          this.forgotPasswordForm.enable();
          this.error = err.error;
          this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
        }
      );
  }

  cancel(): void {
    this.authService.redirectToLoginStep({queryParams: null});
  }

  ngOnDestroy(): void {
    if (this.forgotPasswordSubscription && !this.forgotPasswordSubscription.closed) {
      this.forgotPasswordSubscription.unsubscribe();
    }
    if (this.updatePasswordSubscription && !this.updatePasswordSubscription.closed) {
      this.updatePasswordSubscription.unsubscribe();
    }
  }
}
